import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { Button } from '$partials/ui';
import { IconClose } from '$partials/icons';
import { DetailPageDispatcher } from './detail-page-dispatcher';
import { useKeyboard } from '$hooks/events';
import { useDetailDialog } from './detail-page-provider';
import SideLine from '$partials/global/side-line';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */


const DetailPageModal = ({ isCompanyPage, url }) => {
    const closeButtonRef = useRef();
    const previousPath = useRef();
    const dialogRef = useRef();
    const dialogInnerRef = useRef();

    const [pageData, setPageData] = useState(null);
    const [slideout, setSlideout] = useState(null);
    const [status, setStatus] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(true);

    const { closePage } = useDetailDialog();


    const handleClose = () => {
        window.history.replaceState({ modalUrl: url }, '', previousPath.current);
        window.history.pushState({ modalUrl: url }, '', previousPath.current);

        setSlideout('slide-out');

        setTimeout(() => {
            setIsModalOpen(false);
            closePage();
        }, 250);
    };

    const handlePopState = (event) => {
        if (event.state === null) {
            handleClose();
        }

        if (event.state?.modalUrl !== window.location.pathname) {
            handleClose();
        }
    };

    useKeyboard({
        'Escape': () => isModalOpen && handleClose()
    }, [isModalOpen]);


    useEffect(() => {
        previousPath.current = window.location.pathname;
        window.history.pushState({ modalUrl: url }, '', url);

        window.addEventListener('popstate', handlePopState, { passive: true });

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, []);

    useEffect(() => {
        async function fetchData() {
            try {
                const res = await fetch(`/api/v1/details${url}`);

                setStatus(res.status);
                if (res.status === 200) {
                    const data = await res.json();
                    setPageData(data);
                    setSlideout(null);
                }
            } catch (err) {
                console.error('[DetailPageModal]', err);
            }
        }
        fetchData();
    }, []);


    useEffect(() => {
        // use close/showModal methods to trigger browser's toplayer/inert mode
        const dialogNode = dialogRef.current;
        const dialogInnerNode = dialogInnerRef.current;
        if (isModalOpen) {
            if (dialogNode && !dialogNode.open) {
                disableBodyScroll(dialogInnerNode);
                dialogNode.showModal?.();
            }
        } else {
            if (dialogNode && dialogNode.open) {
                enableBodyScroll(dialogInnerNode);
                dialogNode.close?.();
            }
        }

        return () => {
            enableBodyScroll(dialogInnerNode);
        };
    }, [isModalOpen]);

    // render
    return (
        <dialog
            className='dialog-reset backdrop:backdrop-blur-md'
            onClick={handleClose}
            ref={dialogRef}>
            <div
                className={`w-full overflow-x-scroll fixed top-0 bottom-0 left-0 min-h-screen slide-x-fade-in ${slideout ? '' + slideout : ''}`}
                ref={dialogInnerRef}>

                {/* Container */}
                <div className='layout-container-padding-right max-w-container'>
                    <div
                        className={`bg-white -mr-20 mt-60 sm:ml-10 sm:my-10 min-h-screen relative rounded-t-lg sm:rounded-lg ${isCompanyPage ? 'sm:w-10/12' : 'sm:w-8/12 md:w-8/12'}`}
                        onClick={(e) => e.stopPropagation()}>

                        {/* Sidebar for modals specifically */}
                        <SideLine isInModal={true} />

                        {/* Close button */}
                        <div className='pb-5 pt-15 sm:pt-55 md:pt-45'>
                            <Button
                                className='ml-auto block rounded-full bg-grey p-12 mr-20 sm:mr-55'
                                label='Close modal'
                                look='icon'
                                onClick={handleClose}
                                ref={closeButtonRef}
                                renderIcon={<IconClose color='black'/>}>
                            </Button>
                        </div>

                        {(pageData?.page && status === 200) && (
                            <DetailPageDispatcher
                                canonicalUrl={pageData?.canonicalUrl}
                                page={pageData?.page} />
                        )}

                        {(status !== 200 && !pageData?.page) && (
                            <div className='clearfix col w-full md-col-10 md-ml-col-1 pt12 sm-pt13 md-pt15'>
                                <h1 className='type-style-4'>
                                    {(500 <= status && 599 > status) && (
                                        'Sorry, something went wrong.'
                                    )}

                                    {(400 <= status && 499 > status) && (
                                        'Sorry, we couldn’t find that page.'
                                    )}
                                </h1>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </dialog>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {
    url: PropTypes.string.isRequired,
    isCompanyPage: PropTypes.bool,
};

DetailPageModal.displayName = 'DetailPageModal';
DetailPageModal.propTypes = propTypes;

export { DetailPageModal };
export default DetailPageModal;
